import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../service/Api';
import { useAuth } from './AuthContext';

const SubscriptionLimitsContext = createContext();

/**
 * Hook to access subscription limits data
 * @returns {Object} Subscription limits context
 */
export const useSubscriptionLimits = () => {
  const context = useContext(SubscriptionLimitsContext);
  if (!context) {
    throw new Error('useSubscriptionLimits must be used within a SubscriptionLimitsProvider');
  }
  return context;
};

/**
 * Provider component for subscription limits context
 * Manages subscription plan, usage, and limits data
 */
export const SubscriptionLimitsProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);

  /**
   * Fetch subscription usage data from API
   */
  const fetchUsage = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getSubscriptionUsage();
      
      if (response.data?.success) {
        setUsage(response.data.data);
      } else {
        throw new Error(response.data?.error || 'Failed to fetch subscription usage');
      }
    } catch (err) {
      console.error('❌ Error fetching subscription usage:', err);
      setError(err.message || 'Failed to load subscription data');
      
      // Set default values on error (free tier defaults)
      setUsage({
        plan: {
          name: 'free',
          type: 'free',
          limits: {
            collaterals: 5,
            editsPerCollateral: 5
          }
        },
        usage: {
          collaterals: 0,
          collateralsRemaining: 5
        },
        period: {
          start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
          end: new Date().toISOString(),
          resetsOn: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString()
        },
        subscription: null
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Refresh usage data (call after creating/editing collaterals)
   */
  const refreshUsage = useCallback(() => {
    fetchUsage();
  }, [fetchUsage]);

  /**
   * Check if user can create more collaterals
   */
  const canCreateCollateral = useCallback(() => {
    if (!usage) return false;
    return usage.usage.collateralsRemaining > 0;
  }, [usage]);

  /**
   * Check if user can edit a specific collateral
   * Note: This is a general check - specific slug version counts would need additional API call
   */
  const canEditCollateral = useCallback(() => {
    if (!usage) return false;
    // This is a simplified check - actual edit permission would be verified by backend middleware
    return true; // Backend will enforce the actual limit
  }, [usage]);

  /**
   * Get percentage of limit used
   */
  const getUsagePercentage = useCallback(() => {
    if (!usage || usage.plan.limits.collaterals === 0) return 0;
    return Math.round((usage.usage.collaterals / usage.plan.limits.collaterals) * 100);
  }, [usage]);

  /**
   * Check if user is on free tier
   */
  const isFree = useCallback(() => {
    return usage?.plan?.type === 'free';
  }, [usage]);

  /**
   * Check if limit is reached
   */
  const isLimitReached = useCallback(() => {
    if (!usage) return false;
    return usage.usage.collateralsRemaining <= 0;
  }, [usage]);

  /**
   * Get days until limit resets
   */
  const getDaysUntilReset = useCallback(() => {
    if (!usage?.period?.resetsOn) return null;
    
    const now = new Date();
    const resetDate = new Date(usage.period.resetsOn);
    const diffTime = Math.abs(resetDate - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }, [usage]);

  // Fetch usage when user changes or on mount
  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const value = {
    // Data
    usage,
    loading,
    error,
    
    // Actions
    refreshUsage,
    
    // Helpers
    canCreateCollateral,
    canEditCollateral,
    getUsagePercentage,
    isFree,
    isLimitReached,
    getDaysUntilReset,
    
    // Computed values for convenience
    plan: usage?.plan,
    limits: usage?.plan?.limits,
    currentUsage: usage?.usage,
    subscription: usage?.subscription,
    period: usage?.period
  };

  return (
    <SubscriptionLimitsContext.Provider value={value}>
      {children}
    </SubscriptionLimitsContext.Provider>
  );
};

export default SubscriptionLimitsContext;

